import { Link, graphql, useStaticQuery } from "gatsby";
import { useLenis } from "lenis/react";
import get from "lodash/get";
import React, { useEffect, useState } from "react";
import { ThemeProvider } from "styled-components";
import { THEME_DARK, THEME_DEFAULT } from "../../../theme/theme";
import { getPathFromModel } from "../../../utils/link";
import { ButtonLink } from "../../common/button-link";
import { StyledBannerContainer, StyledBannerLinks, StyledBurger, StyledButtonContainer, StyledHeaderContainer, StyledHeaderContent, StyledLink, StyledLogo, StyledMenuContainer, StyledMenuLinks } from "./header.styles";

const LinkItem = ({
  link,
  onLinkClick
}) => {
  let props;
  if (link.model.apiKey === "link_page") {
    const url = getPathFromModel({ ...link.page })
    props = {
      as: Link,
      to: url
    }
  }
  else if (link.model.apiKey === "link_url") {
    props = {
      as: "a",
      target: "_blank",
      href: link.url
    }
  }
  else if (link.model.apiKey === "link_phone") {
    props = {
      as: "a",
      href: `tel:${link.phone}`
    }
  }

  if (props) {
    return (
      <li><StyledLink {...props} onClick={onLinkClick}>{link.label}</StyledLink></li>
    )
  }

  return null;
};

const LinkGroup = ({ label, links, onLinkClick, menuOpen }) => {

  const [isOpen, setIsOpen] = useState(false);
  const [isOver, setIsOver] = useState(false);

  const handleClick = () => {
    setIsOpen(!isOpen);
  }

  const handleOver = () => {
    setIsOver(true);
  }

  const handleOut = () => {
    setIsOver(false);
  }

  useEffect(() => {
    if (!menuOpen) {
      setIsOpen(false);
    }
  }, [menuOpen]);

  return (
    <li data-over={isOver} data-open={isOpen} onMouseOver={handleOver} onMouseOut={handleOut}>
      <span onClick={handleClick} >{label}</span>
      <ul>
        {links.map(link => <LinkItem link={link} onLinkClick={onLinkClick} />)}
      </ul>
    </li>
  )
}

export const Header = ({ dark, isCompact }: { dark?: boolean, isCompact?: boolean }) => {
  const data = useStaticQuery(graphql`
		query HeaderQuery {
			header:datoCmsHeader {
				menuLinks {
          ...LinkPageFragment
          ...LinkUrlFragment
          ...LinkFormFragment
          ...LinkPhoneFragment
          ...LinkGroupFragment
        }
        bannerLinks {
          ...LinkPageFragment
          ...LinkUrlFragment
          ...LinkFormFragment
          ...LinkPhoneFragment
          ...LinkGroupFragment
        }
        primaryLink {
          ...LinkPageFragment
          ...LinkUrlFragment
          ...LinkFormFragment
          ...LinkPhoneFragment
        }
      }
		}
	`)

  const header = get(data, "header");

  if (!header) return null;

  const [scrollY, setScrollY] = useState(0);
  const [sticky, setSticky] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    // window.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      // window.removeEventListener('click', handleClick);
    }
  }, []);

  const lenis = useLenis();
  useEffect(() => {

    if (lenis) {
      if (open) {
        lenis.stop();
      }
      else {
        lenis.start();
      }
    }
    return () => {
      if (lenis) {
        lenis.start();
      }
    }
  }, [open])

  const handleScroll = () => {
    setScrollY(window.scrollY);

    if (window.scrollY > 30) {
      setSticky(true);
    } else {
      setSticky(false);
    }
  };

  const handleBurgerClick = () => {
    setOpen(!open);
  }

  return (
    <ThemeProvider theme={dark ? THEME_DARK : THEME_DEFAULT}>
      <StyledHeaderContainer $sticky={sticky} $open={open}>
        <StyledLogo to="/" onClick={() => setOpen(false)}>
          <svg viewBox="0 0 165 45" xmlns="http://www.w3.org/2000/svg">
            <path d="M119.285 37.3748H120.229V36.7548H116.896V37.3748H117.839L115.198 44.1941H114.381V44.814H116.896V44.1941H115.89L116.77 41.9003H120.229L121.109 44.1941H120.166V44.814H122.681V44.1941H121.864L119.285 37.3748ZM117.022 41.3424L118.531 37.3128L120.04 41.3424H117.022ZM158.649 9.4155C154.876 8.54758 153.996 7.86565 153.996 6.31581C153.996 5.01393 155.191 3.96004 157.203 3.96004C159.026 3.96004 160.787 4.64197 162.548 5.94384L164.371 3.3401C162.359 1.79025 160.095 0.922333 157.266 0.922333C153.367 0.922333 150.537 3.21611 150.537 6.56378C150.537 10.1594 152.927 11.3993 157.077 12.3912C160.724 13.1971 161.542 13.9411 161.542 15.3669C161.542 16.8548 160.221 17.8467 158.083 17.8467C155.631 17.8467 153.744 16.9168 151.858 15.3049L149.783 17.7227C152.172 19.7685 155.002 20.8224 157.957 20.8224C162.107 20.8224 165 18.6526 165 14.995C165 11.8953 162.799 10.4074 158.649 9.4155ZM136.452 14.685L125.951 1.2943H122.744V20.6984H126.139V6.87375L136.955 20.6364H139.848V1.2943H136.452V14.685ZM86.3357 1.2943H82.8773V20.6984H86.3357V1.2943ZM96.3967 40.4125C95.2649 39.9785 94.4474 39.6065 94.4474 38.6766C94.4474 37.8707 95.0762 37.2508 96.1452 37.2508C96.6482 37.2508 97.3399 37.4368 97.5915 37.7467C97.4657 37.8707 97.3399 38.0567 97.3399 38.2427C97.3399 38.5526 97.5915 38.8006 97.9059 38.8006C98.2203 38.8006 98.4718 38.5526 98.4718 38.1807C98.4718 37.3748 97.4028 36.6308 96.1452 36.6308C94.636 36.6308 93.6928 37.4988 93.6928 38.6766C93.6928 40.0405 94.7618 40.5365 96.1452 41.0944C97.0884 41.4664 97.9059 41.9003 97.9059 42.7682C97.9059 43.6981 97.2142 44.3181 96.0194 44.3181C95.202 44.3181 94.5103 44.0701 94.2588 43.7601C94.3845 43.6362 94.5103 43.4502 94.5103 43.2022C94.5103 42.8922 94.2588 42.7062 94.0072 42.7062C93.6928 42.7062 93.4413 42.9542 93.4413 43.3882C93.4413 44.0701 94.4474 44.938 96.0823 44.938C97.5286 44.938 98.6604 44.0701 98.6604 42.7062C98.6604 41.4044 97.6543 40.9084 96.3967 40.4125ZM103.817 37.3748H104.886V44.1941H103.817V44.814H109.665V43.0162H108.973V44.2561H105.64V41.0324H108.407V40.4125H105.64V37.3748H109.036V38.6146H109.728V36.8168H103.88V37.3748H103.817ZM87.6562 44.2561H84.5751V37.4368H85.7069V36.8168H82.7515V37.4368H83.8205V44.2561H82.7515V44.876H88.3479V43.0162H87.6562V44.2561ZM63.6986 37.3748H64.7675V40.3505H59.9257V37.3748H60.9947V36.7548H58.1021V37.3748H59.1711V44.1941H58.1021V44.814H60.9947V44.1941H59.9257V40.9704H64.7675V44.1941H63.6986V44.814H66.5911V44.1941H65.5221V37.3748H66.5911V36.7548H63.6986V37.3748ZM8.67759 1.17031L0 20.6984H3.52134L5.53354 16.0489H14.8399L16.8521 20.6984H20.4992L11.8216 1.17031H8.67759ZM6.79116 12.9492L10.1867 5.19991L13.5823 12.9492H6.79116ZM49.9276 44.3801C47.9154 44.3801 46.532 42.6442 46.532 40.9084C46.532 39.0486 47.9154 37.3128 49.9276 37.3128C51.1223 37.3128 51.9398 37.8707 52.1284 38.3047C51.9398 38.3667 51.6883 38.5526 51.6883 38.8626C51.6883 39.1726 51.9398 39.4206 52.2542 39.4206C52.6315 39.4206 52.883 39.1106 52.883 38.7386C52.883 37.7467 51.4996 36.7548 49.8647 36.7548C47.7268 36.7548 45.7774 38.4907 45.7774 40.9704C45.7774 43.0162 47.4123 45.062 49.9276 45.062C50.9966 45.062 52.0655 44.69 52.9459 43.8841L52.5686 43.3882C51.7511 44.0081 50.9337 44.3801 49.9276 44.3801ZM45.8403 1.2943H42.3819V12.4532C42.3819 16.0489 40.4954 17.8467 37.4143 17.8467C34.3331 17.8467 32.4466 15.9249 32.4466 12.3292V1.2943H28.9882V12.4532C28.9882 18.0947 32.258 21.0084 37.3514 21.0084C42.5076 21.0084 45.7774 18.0947 45.7774 12.3292V1.2943H45.8403ZM101.176 12.4532H111.048V9.4155H101.176V4.332H112.306V1.2943H97.7172V20.6984H112.431V17.6607H101.176V12.4532ZM71.6216 37.3748H72.6905V44.1941H71.6216V44.814H77.4695V43.0162H76.7778V44.2561H73.3822V41.0324H76.149V40.4125H73.3822V37.3748H76.7778V38.6146H77.4695V36.8168H71.6216V37.3748ZM67.5972 13.3211C70.3639 12.5772 72.3133 10.6554 72.3133 7.43169C72.3133 3.65006 69.5465 1.2943 65.0191 1.2943H56.2157V20.6984H59.6742V13.9411H64.013L68.8548 20.6984H72.9421L67.5972 13.3211ZM59.6742 10.9034V4.39399H64.7047C67.2828 4.39399 68.7919 5.50988 68.7919 7.61768C68.7919 9.60148 67.2199 10.9034 64.7675 10.9034H59.6742Z" />
          </svg>
        </StyledLogo>
        <StyledBurger onClick={handleBurgerClick} />
        <StyledHeaderContent>
          <StyledBannerContainer>
            <StyledBannerLinks>
              {header.bannerLinks.map(link => link.model.apiKey === "link_group" ? <LinkGroup {...link} menuOpen={open} onLinkClick={() => setOpen(false)} /> : <LinkItem link={link} onLinkClick={() => setOpen(false)} />)}
            </StyledBannerLinks>
          </StyledBannerContainer>
          <StyledMenuContainer data-lenis-prevent>
            <StyledMenuLinks $isCompact={isCompact}>
              {header.menuLinks.map(link => link.model.apiKey === "link_group" ? <LinkGroup {...link} menuOpen={open} onLinkClick={() => setOpen(false)} /> : <LinkItem link={link} onLinkClick={() => setOpen(false)} />)}
            </StyledMenuLinks>
          </StyledMenuContainer>
          <StyledButtonContainer>
            <ButtonLink link={header.primaryLink} variant="primary" />
          </StyledButtonContainer>
        </StyledHeaderContent>
      </StyledHeaderContainer>
    </ThemeProvider>
  )
}